/**
 * Shared TagPrefixModal Component
 *
 * Reusable modal for creating and managing tag prefixes across survey types.
 * Used by: SatisfactionSurvey, CustomSurvey, EventRegistrationSurvey
 *
 * Features:
 * - Create new tag prefixes from question labels
 * - Edit existing tag prefixes
 * - Keyboard shortcuts (Enter to save, Escape to cancel)
 * - Input validation and auto-trimming
 * - Consistent modal styling
 */

import { css, StyleSheet } from 'aphrodite';
import React, { useState } from 'react';
import { Button } from '../../../../../components/Button';
import { Modal } from '../../../../../components/Modal';
import { TextInputFormField } from '../../../../../components/TextInputFormField';
import { bs } from '../../../../../styles/styles';

/**
 * Props for TagPrefixModal component
 */
interface TagPrefixModalProps {
	isOpen: boolean;
	onRequestClose: (result?: { fieldId: string; tagPrefix: string } | null, canceled?: boolean) => void;
	questionLabel: string;
	fieldId: string;
	hasExistingPrefix?: boolean;
	className?: string;
}

/**
 * Reusable TagPrefixModal component
 */
export const TagPrefixModal: React.FC<TagPrefixModalProps> = ({
	isOpen,
	onRequestClose,
	questionLabel,
	fieldId,
	hasExistingPrefix = false,
	className,
}) => {
	const [tagPrefix, setTagPrefix] = useState(questionLabel);

	// Reset the input to questionLabel when modal opens
	React.useEffect(() => {
		if (isOpen) {
			setTagPrefix(questionLabel);
		}
	}, [isOpen, questionLabel]);

	const handleSave = () => {
		if (tagPrefix.trim()) {
			onRequestClose({ fieldId, tagPrefix: tagPrefix.trim() }, false);
		}
	};

	const handleCancel = () => {
		onRequestClose(null, true);
	};

	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			handleSave();
		} else if (e.key === 'Escape') {
			handleCancel();
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onRequestClose={handleCancel}
			useStyledHeader={true}
			shouldCloseOnOverlayClick={true}
			className={className}
		>
			<section>
				<h2 className={css(styles.modalTitle)}>
					{hasExistingPrefix ? 'Manage Tag Prefix' : 'Create Tags from Answers'}
				</h2>
				<p className={css(styles.modalDescription)}>
					{hasExistingPrefix ? 'Edit or delete tags created by answers' : 'Answers will be prepended with the tag name'}
				</p>

				<div className={css(bs.mb4)}>
					<TextInputFormField
						id='tag-prefix-input'
						label='Tag Prefix'
						placeholder='Enter tag prefix...'
						value={tagPrefix}
						onChange={e => setTagPrefix(e.target.value)}
						onKeyDown={handleKeyDown}
						type='text'
						styles={[styles.modalInput]}
					/>
				</div>

				<footer className={css(bs.flex, bs.gap2)}>
					<Button
						type='button'
						kind='primary'
						onClick={handleSave}
						label={hasExistingPrefix ? 'Save' : 'Add'}
						className={css(styles.modalSaveButton)}
						disabled={!tagPrefix.trim()}
					/>
					<Button
						type='button'
						kind='reverse'
						onClick={handleCancel}
						label='Cancel'
						className={css(styles.modalCancelButton)}
					/>
				</footer>
			</section>
		</Modal>
	);
};

/**
 * Styles for TagPrefixModal component
 */
const styles = StyleSheet.create({
	modalTitle: {
		fontSize: '18px',
		fontWeight: 600,
		color: '#1a1a1a',
		margin: '0 0 12px 0',
	},

	modalDescription: {
		fontSize: '14px',
		color: '#666666',
		margin: '0 0 20px 0',
		lineHeight: '1.4',
	},

	modalInput: {
		width: '100%',
		fontSize: '14px',
		border: 'none',
		borderRadius: '4px',
		outline: 'none',
		boxSizing: 'border-box',

		':focus': {
			borderColor: '#0066cc',
			boxShadow: '0 0 0 3px rgba(0, 102, 204, 0.1)',
		},
	},

	modalSaveButton: {
		minWidth: '80px',
		height: '36px',
		fontSize: '14px',
		fontWeight: 500,
	},

	modalCancelButton: {
		minWidth: '80px',
		height: '36px',
		fontSize: '14px',
		fontWeight: 500,
	},
});

/**
 * Type exports for consumers
 */
export type { TagPrefixModalProps };
